import { useState, useRef, memo, useMemo, useEffect } from 'react';
import { Box, Paper, Popper, Typography } from '@mui/material';

const isOverflown = (element) => {
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
};

const GridCellExpand = memo((props) => {
  const { width, value } = props;
  const wrapper = useRef(null);
  const cellDiv = useRef(null);
  const cellValue = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showFullCell, setShowFullCell] = useState(false);
  const [showPopper, setShowPopper] = useState(false);
  const [popperWidth, setPopperWidth] = useState(null);

  const hasBreaksOrTabs = typeof value === 'string' && /[\r\n\t]/.test(value);
  const previewValue = useMemo(() => {
    if (value == null) return '';
    const str = String(value);
    const firstLine = str.split(/\r?\n/)[0].replace(/\t/g, '    ');
    return hasBreaksOrTabs ? `${firstLine} …` : firstLine;
  }, [value, hasBreaksOrTabs]);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current);

    setShowPopper(isCurrentlyOverflown || hasBreaksOrTabs);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);

    const contentScrollWidth = cellValue.current?.scrollWidth ?? 0;
    const minWidth = Math.max(width || 0, wrapper.current?.offsetWidth || 0);
    const left = wrapper.current?.getBoundingClientRect().left ?? 0;
    const rightSpace = Math.max(0, window.innerWidth - left - 24);
    const desired = Math.max(minWidth, contentScrollWidth + 16);
    const finalWidth = Math.min(desired, rightSpace);
    setPopperWidth(finalWidth);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    const handleKeyDown = (nativeEvent) => {
      if (nativeEvent.key === 'Escape') {
        setShowFullCell(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: 'center',
        lineHeight: '24px',
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: '100%',
          width: '100%',
          display: 'block',
          position: 'absolute',
          top: 0,
        }}
      />
      <Box ref={cellValue} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {previewValue}
      </Box>
      {showPopper && (
        <Popper
          open={showFullCell && anchorEl !== null}
          anchorEl={anchorEl}
          style={{
            maxWidth: popperWidth ?? undefined,
            marginLeft: -17,
            zIndex: 10000,
          }}
        >
          <Paper elevation={1} style={{ minHeight: wrapper.current ? wrapper.current.offsetHeight - 3 : undefined }}>
            <Typography
              variant="body2"
              style={{
                padding: 8,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                tabSize: 4,
              }}
            >
              {value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});
GridCellExpand.displayName = 'GridCellExpand';

export default GridCellExpand;
